import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LoggingService } from '@services/logging.service';
import { BDMatrixFilter } from '@models/bdMatrixFilter.model';
import { InternetReview } from '@models/internetreview.model';
import { BDMatrixEliminationCriteria } from '@models/bdMatrixEliminationCriteria.model';
import { InternetReviewMatrixResultView } from '@models/internetReviewMatrixResultView';
import { Website } from '@models/website.model';
import { InternetReviewScreenResult } from '@models/internetReviewScreenResult.model';
import { firstValueFrom, Observable } from 'rxjs';
import _ from 'lodash-es';
import { InternetReviewBulkAccept } from '../mystudies/analysis/internetreview/ir-bulk-accept.model';
import { IrNonBvdComparableDetails } from '../mystudies/analysis/internetreview/internet-review-detail/ir-nonbvd-details/ir-non-bvd-comparable-details.model';
import { UserNotificationService } from '@services/user-notification.service';
import { saveAs } from 'file-saver-es';
import { FinancialDatAdjustmentErrorMessage } from '../shared/_constants';

@Injectable({
  providedIn: 'root',
})
export class InternetReviewService {
  public comparableIndex: number;
  public comparableCount: number;
  public hasBlankComparable: boolean;
  public selectedComparables: InternetReviewMatrixResultView[] = [];
  public filter: BDMatrixFilter = new BDMatrixFilter();
  public comparablesWithErrors: number[] = [];
  public isImportCompleted: EventEmitter<boolean> = new EventEmitter();

  get itemIndex(): number {
    return this.filter.pageIndex * this.filter.pageSize + this.comparableIndex;
  }

  constructor(
    private http: HttpClient,
    private logger: LoggingService,
    private userNotificationService: UserNotificationService,
  ) {}

  public getSelectedComparables(): InternetReviewMatrixResultView[] {
    return this.selectedComparables;
  }

  public updateComparableIfSelected(comparable: InternetReviewMatrixResultView) {
    const itemIndex = _.findIndex(this.selectedComparables, { comparableID: comparable.comparableID });

    if (itemIndex !== -1) {
      this.selectedComparables[itemIndex] = comparable;
    }
  }

  public async getInternetReviewMatrix(bdMatrixFilter: BDMatrixFilter): Promise<InternetReviewScreenResult> {
    this.logger.info(this.constructor.name, 'GetInternetReviewMatrix', 'Start');
    const result = <InternetReviewScreenResult>await firstValueFrom(
      this.http.post<InternetReviewScreenResult>('/api/InternetReview/GetInternetReviewResult/', bdMatrixFilter),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetInternetReviewMatrix', e);
    });

    return result;
  }

  public async UpdateInternetReview(internetReview: InternetReview): Promise<InternetReview> {
    this.logger.info(this.constructor.name, 'UpdateInternetReview', 'Start');

    const result = <InternetReview>await firstValueFrom(
      this.http.put<InternetReview>('/api/InternetReview/UpdateInternetReview', internetReview),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'UpdateInternetReview', e);
    });
    return result;
  }

  public async AddWebsite(website: Website): Promise<Website> {
    this.logger.info(this.constructor.name, 'AddWebsite', 'Start');

    const result = <Website>await firstValueFrom(this.http.post<Website>('/api/InternetReview/AddWebsite', website)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'AddWebsite', e);
    });
    return result;
  }

  public async UpdateUseSuggestedWebsite(internetReview: InternetReview): Promise<boolean> {
    this.logger.info(this.constructor.name, 'UpdateUseSuggestedWebsite', 'Start', internetReview);
    const saveResult: boolean = <boolean>await firstValueFrom(
      this.http.put<boolean>('/api/InternetReview/UpdateUseSuggestedWebsite', internetReview),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'UpdateUseSuggestedWebsite', e);
    });
    return saveResult;
  }

  public async SaveEliminationCriteria(bdMatrixEliminationCriteria: BDMatrixEliminationCriteria) {
    this.logger.info(this.constructor.name, 'SaveEliminationCriteria', 'Start');
    <string>await firstValueFrom(this.http.post<string>('/api/InternetReview/EliminationCriteria/', bdMatrixEliminationCriteria)).catch((e) => {
      this.logger.httpError(this.constructor.name, 'SaveEliminationCriteria', e);
    });
  }

  public async SaveEliminationCriteriaRange(bdMatrixEliminationCriteriaList: Array<BDMatrixEliminationCriteria>) {
    this.logger.info(this.constructor.name, 'SaveEliminationCriteriaRange', 'Start');
    <string>await firstValueFrom(this.http.post<string>('/api/InternetReview/EliminationCriteriaRange/', bdMatrixEliminationCriteriaList)).catch(
      (e) => {
        this.logger.httpError(this.constructor.name, 'SaveEliminationCriteriaRange', e);
      },
    );
  }

  public async bulkAccept(bulkAccept: InternetReviewBulkAccept): Promise<boolean> {
    this.logger.info(this.constructor.name, 'BulkAccept', 'Start', bulkAccept);
    const saveResult: boolean = <boolean>await firstValueFrom(this.http.post<boolean>('/api/InternetReview/BulkAcceptComparable', bulkAccept)).catch(
      (e) => {
        this.logger.httpError(this.constructor.name, 'BulkAccept', e);
      },
    );
    return saveResult;
  }

  public getItem(searchStrategyId: number): Promise<InternetReviewScreenResult> {
    this.logger.info(this.constructor.name, 'getItem', 'Start');
    const itemIndex = this.filter.pageIndex * this.filter.pageSize + this.comparableIndex;
    const filter = Object.assign({}, this.filter);
    filter.pageSize = 1;
    filter.pageIndex = itemIndex;
    filter.studySearchStrategyId = searchStrategyId;
    return this.getInternetReviewMatrix(filter);
  }

  public executeScrape(
    searchStrategyId: number,
    includeKeywords: string,
    excludeKeywords: string,
    comparableId: number = null,
    limitscraping: any,
    depthToScrape: number,
    numberOfPages: number,
    includeNativeLanguage: boolean,
  ): Observable<any> {
    const body = {
      studySearchStrategyId: searchStrategyId,
      includeKeywords: includeKeywords,
      excludeKeywords: excludeKeywords,
      limitscraping: limitscraping,
      depthToScrape: depthToScrape,
      numberOfPages: numberOfPages,
      includeNativeLanguage: includeNativeLanguage,
    };
    if (comparableId) {
      body['comparableIds'] = [{ id: comparableId }];
    } else if (this.selectedComparables && this.selectedComparables.length > 0) {
      body['comparableIds'] = this.selectedComparables.map((c) => ({ id: c.comparableID }));
    }
    return this.http.post<string>('/api/InternetReview/ExecuteScrape/', body);
  }

  public downloadScrapedFile(studyId: number): Observable<any> {
    return this.http.get('/api/InternetReview/DownloadScrapedFile/' + studyId, { observe: 'response', responseType: 'blob' as 'json' });
  }

  public async checkBlanksCount(searchStreategyId: number) {
    this.hasBlankComparable = await this.checkInternetReviewBlanks(searchStreategyId);
  }

  public async checkInternetReviewBlanks(searchStreategyId: number): Promise<boolean> {
    const result = <boolean>await firstValueFrom(this.http.get<boolean>(`/api/InternetReview/CheckInternetReviewBlanks/${searchStreategyId}`)).catch(
      (e) => {
        this.logger.httpError(this.constructor.name, 'CheckInternetReviewBlanks', e);
      },
    );

    return result;
  }

  public removeComparableFromErrorList(comparable: InternetReview): void {
    this.comparablesWithErrors = this.comparablesWithErrors.filter((c) => c !== comparable.comparableId);
  }

  public async getNonBvdComparableDetails(
    comparableId: number,
    databaseId: number,
    feedId: number,
    searchStrategyId: number,
  ): Promise<IrNonBvdComparableDetails> {
    this.logger.info(this.constructor.name, 'getNonBvdComparableDetails', 'Start');

    const result = <IrNonBvdComparableDetails>await firstValueFrom(
      this.http.get<IrNonBvdComparableDetails>(
        '/api/InternetReview/GetNonBvdComparableDetailsForIR/?comparableId=' +
          comparableId +
          '&databaseId=' +
          databaseId +
          '&feedId=' +
          feedId +
          '&searchStrategyId=' +
          searchStrategyId,
      ),
    ).catch((e) => {
      this.logger.httpError(this.constructor.name, 'GetNonBvdComparableDetails', e);
    });

    return result;
  }

  async exportMatrix(studyId: number): Promise<boolean> {
    const result = await firstValueFrom(
      this.http.get('/api/InternetReview/ExportMatrix/' + studyId, {
        responseType: 'blob',
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        observe: 'response',
      }),
    )
      .then((response) => {
        let filename = 'Temp_';
        const contentDispositionHeader = response.headers.get('Content-Disposition');

        if (contentDispositionHeader) {
          filename = contentDispositionHeader.split(';')[1].trim().split('=')[1];
        }

        const blob = new Blob([response.body], { type: response.body.type.toString() });
        saveAs(blob, filename);

        this.userNotificationService.addNotification(`Internet Review Excel file for ${studyId}: Ready to download.`, false);
        return true;
      })
      .catch((e) => {
        this.logger.httpError(this.constructor.name, 'exportMatrix', e);
        if (e.status === 404) {
          this.userNotificationService.addNotification(`Internet Review Excel file failed to download for study ${studyId}: No data found.`, true);
        }

        return false;
      });
    return result;
  }

  async importMatrix(formdata: FormData, returnurl: string, studyId: number): Promise<boolean> {
    const fileName = formdata.get('fileName');
    this.userNotificationService.addNotification(
      `Import in Progress: Your file '${fileName}' is being imported into Internet Review Matrix. Please note that this process may take a few moments, particularly for larger files. for studyId  ${studyId}.`,
      false,
    );

    const result = await firstValueFrom(this.http.post('/api/InternetReview/importmatrix/', formdata))
      .then(() => {
        this.userNotificationService.addNotification(
          `Import Complete: Your file '${fileName}' has been successfully imported into Internet Review Matrix for studyId  ${studyId}.`,
          false,
        );
        this.isImportCompleted.emit(true);
        return true;
      })
      .catch((e) => {
        if (e.status === 404) {
          this.userNotificationService.addNotification(
            `Import Unsuccessful – to ensure a successful import, kindly verify the following:

            The file you're importing corresponds to the same Search Strategy used at the time of export - any changes to the Search Strategy since the export can lead to a failed import.
            The order and number of columns in your file exactly match those in the exported file.
            There are no duplicate rows with the same Company ID and Company Name in the file.
            No new companies have been added to the list after export for study ${studyId}: No data found.`,
            true,
          );
          return null;
        } else {
          let resultMsg = FinancialDatAdjustmentErrorMessage[e.error.toString()];
          if (!resultMsg) {
            resultMsg = e.error.toString();
          }
          this.userNotificationService.addNotification(
            `Import Unsuccessful – to ensure a successful import, kindly verify the following:

            The file you're importing corresponds to the same Search Strategy used at the time of export - any changes to the Search Strategy since the export can lead to a failed import.
            The order and number of columns in your file exactly match those in the exported file.
            There are no duplicate rows with the same Company ID and Company Name in the file.
            No new companies have been added to the list after export ${studyId}:${resultMsg}`,
            true,
          );
        }
        return false;
      });
    return result;
  }
}
